<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { UiText } from '@shopexperts/shared';
import { onBeforeRouteLeave } from 'vue-router';

import ProjectDetailsStep, { type ProjectDetailsData } from './leads/ProjectDetailsStep.vue';
import StoreInformationStep, { type StoreInformationData } from './leads/StoreInformationStep.vue';
import AuthStep, { type AuthenticatedData } from './leads/AuthStep.vue';
import SuccessStep from './leads/SuccessStep.vue';

import { useCreateStore } from '@/app/composables/leads/useCreateStore';
import { useAuthCheck } from '@/app/composables/leads/useAuthCheck';
import { useExpertMinimal } from '@/app/composables/leads/useExpertMinimal';
import { useClientStores } from '@/app/composables/leads/useClientStores';
import { useSubmitLead } from '@/app/composables/leads/useSubmitLead';
import { useMachine } from '@xstate/vue';
import { leadSubmissionMachine } from '@/app/composables/leads/useLeadSubmissionStateMachine';
import { usePostHog } from '@/app/composables/usePostHog';

// Initialize PostHog tracking
const {
  trackLeadFormStarted,
  trackLeadFormStepViewed,
  trackLeadFormStepCompleted,
  trackLeadFormBackNavigation,
  trackLeadFormSubmitted,
  trackLeadFormSubmitSuccess,
  trackLeadFormSubmitError,
  trackLeadFormAbandoned,
} = usePostHog();

interface Props {
  directThreadId?: string;
  directPeerName?: string;
  expertSlug?: string;
  packagedServiceSlug?: string;
  disableAutoRedirect?: boolean;
  source?: "expert" | "client";
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'success'): void;
}>();

// ==================
// Auth & User State
// ==================

const { isAuthenticated, isClient, client } = useAuthCheck();
const isAuthenticatedClient = computed(() => isAuthenticated.value && isClient.value);

// ==================
// State Machine
// ==================

const { snapshot, send } = useMachine(leadSubmissionMachine);

const currentState = computed(() => snapshot.value.value as 'projectDetails' | 'storeInformation' | 'auth' | 'success');
const totalSteps = computed(() => isAuthenticatedClient.value ? 2 : 3);

const submissionSuccess = ref(false);
const submitError = ref<string | null>(null);

// Track if form has unsaved changes
const hasUnsavedChanges = computed(() => {
  if (submissionSuccess.value) return false;
  
  // Check if any form data has been entered
  const hasProjectDetails = !!projectDetailsData.value.projectTitle || 
                           !!projectDetailsData.value.projectDescription ||
                           projectDetailsData.value.projectValueRangeId !== undefined;
  const hasStoreInfo = storeInformationData.value.storeMode === 'existing' && 
                      (!!storeInformationData.value.storeId || 
                       !!storeInformationData.value.storeName || 
                       !!storeInformationData.value.storeUrl);
  const hasCompanyName = !!companyName.value;
  
  return hasProjectDetails || hasStoreInfo || hasCompanyName;
});

// ==================
// Form Data
// ==================

const projectDetailsData = ref<ProjectDetailsData>({
  projectTitle: '',
  projectDescription: '',
  urgency: undefined,
  projectValueRangeId: undefined,
});

const storeInformationData = ref<StoreInformationData>({
  storeMode: 'new',
  storeId: undefined,
  storeUrl: '',
  storeName: '',
  storePlan: 'basic' as const,
});

const companyName = ref<string>('');

// ==================
// Queries
// ==================

const { expert, packagedService, isLoading: isLoadingExpert } = useExpertMinimal({
  expertSlug: () => props.expertSlug,
  packagedServiceSlug: () => props.packagedServiceSlug,
});
const { stores: clientStores, isLoading: isLoadingStores } = useClientStores(isAuthenticatedClient);

const selectedExpertName = computed(() => {
  if (props.directPeerName) return props.directPeerName;
  return props.expertSlug ? expert.value?.name : undefined;
});

const storeNameForPrefill = computed(() => {
  const storeData = storeInformationData.value;
  
  if (storeData.storeMode === 'new') {
    return '';
  }
  
  if (storeData.storeMode === 'existing' && storeData.storeId) {
    const selectedStore = clientStores.value.find(store => store.id === storeData.storeId);
    return selectedStore?.title || '';
  }
  
  if (storeData.storeMode === 'existing' && storeData.storeName) {
    return storeData.storeName;
  }
  
  return '';
});

const storeUrlForPrefill = computed(() => {
  const storeData = storeInformationData.value;
  
  if (storeData.storeMode === 'new') {
    return '';
  }
  
  if (storeData.storeMode === 'existing' && storeData.storeId) {
    const selectedStore = clientStores.value.find(store => store.id === storeData.storeId);
    return selectedStore?.url || '';
  }
  
  if (storeData.storeMode === 'existing' && storeData.storeUrl) {
    return storeData.storeUrl;
  }
  
  return '';
});

// Pre-fill project title and description with packaged service name when it loads
watch([packagedService, expert], ([newService, expertData]) => {
  if (newService?.name) {
    if (!projectDetailsData.value.projectTitle) {
      projectDetailsData.value.projectTitle = newService.name;
    }
    if (!projectDetailsData.value.projectDescription && expertData?.name) {
      const expertFirstName = expertData.name.split(' ')[0];
      projectDetailsData.value.projectDescription = `Hey ${expertFirstName},\n\nI'm interested in proceeding with the ${newService.name} packaged service.`;
    } else if (!projectDetailsData.value.projectDescription) {
      projectDetailsData.value.projectDescription = `I'm interested in proceeding with the ${newService.name} packaged service.`;
    }
  }
}, { immediate: true });

// ==================
// Mutations
// ==================

const { createStore } = useCreateStore();
const { submitLead: submitLeadMutation } = useSubmitLead({ showToast: false });

// ==================
// Step Handlers
// ==================

function handleProjectDetailsNext(data: ProjectDetailsData) {
  projectDetailsData.value = data;
  
  // Track step completion
  trackLeadFormStepCompleted('projectDetails', 1, {
    has_project_description: !!data.projectDescription,
    urgency: data.urgency,
  });
  
  send({ type: 'NEXT' });
  
  // Track next step view
  trackLeadFormStepViewed('storeInformation', 2, totalSteps.value);
}

async function handleStoreInformationNext(data: StoreInformationData) {
  storeInformationData.value = data;
  
  // Track step completion
  trackLeadFormStepCompleted('storeInformation', 2, {
    store_mode: data.storeMode,
    has_existing_store: !!data.storeId,
  });
  
  // Use the REACTIVE auth state to decide the path
  if (isAuthenticatedClient.value) {
    if (await submitLead()) {
      send({ type: 'SKIP_TO_SUCCESS' });
    }
  } else {
    // User needs to authenticate - go to auth step
    send({ type: 'NEXT' });
    
    // Track auth step view
    trackLeadFormStepViewed('auth', 3, totalSteps.value);
  }
}

function handleStoreInformationBack() {
  trackLeadFormBackNavigation('storeInformation', 'projectDetails');
  send({ type: 'BACK' });
  trackLeadFormStepViewed('projectDetails', 1, totalSteps.value);
}

function handleAuthBack() {
  trackLeadFormBackNavigation('auth', 'storeInformation');
  send({ type: 'BACK' });
  trackLeadFormStepViewed('storeInformation', 2, totalSteps.value);
}

async function handleAuthenticated(data: AuthenticatedData) {
  if (data.companyName) {
    companyName.value = data.companyName;
  }
  
  // Track auth step completion
  trackLeadFormStepCompleted('auth', 3);
  
  const success = await submitLead();
  if (success) {
    send({ type: 'SUBMIT' });
  }
}

// ==================
// Lead Submission
// ==================

async function submitLead(): Promise<boolean> {
  submitError.value = null;
  try {
    let storeId = storeInformationData.value.storeId;
    
    if (storeInformationData.value.storeMode === 'new') {
      const storeName = companyName.value || client.value?.company_name || 'My Store';
      const placeholderUrl = `https://placeholder-${Date.now()}.myshopify.com`;
      
      const store = await createStore({
        store_url: placeholderUrl,
        store_name: storeName,
        shopify_plan: storeInformationData.value.storePlan,
      });
      storeId = store.id;
    } else if (storeInformationData.value.storeMode === 'existing' && !storeId) {
      const store = await createStore({
        store_url: storeInformationData.value.storeUrl || '',
        store_name: storeInformationData.value.storeName || '',
        shopify_plan: storeInformationData.value.storePlan,
      });
      storeId = store.id;
    }
    
    if (!storeId) {
      throw new Error('Store ID is required');
    }
    
    const hasPreferredExpert = !!props.expertSlug && !!expert.value;
    const matchingStrategy = (props.directThreadId || hasPreferredExpert) ? 'selected' : 'auto';
    
    let selectedExpertId: number | undefined;
    if (hasPreferredExpert && expert.value?.id) {
      selectedExpertId = expert.value.id;
    }
    
    // Include packaged service ID if selected
    let selectedPackagedServiceId: number | undefined;
    if (packagedService.value?.id) {
      selectedPackagedServiceId = packagedService.value.id;
    }
    
    // Track form submission attempt
    trackLeadFormSubmitted({
      hasPreferredExpert,
      hasPackagedService: !!selectedPackagedServiceId,
      urgency: projectDetailsData.value.urgency,
      storeMode: storeInformationData.value.storeMode,
      matchingStrategy,
    });
    
    await submitLeadMutation({
      directThreadId: props.directThreadId,
      storeId,
      projectTitle: projectDetailsData.value.projectTitle,
      projectBrief: projectDetailsData.value.projectDescription || '',
      urgency: projectDetailsData.value.urgency || 'open_to_expert_opinion',
      projectValueRangeId: projectDetailsData.value.projectValueRangeId,
      matchingStrategy,
      selectedExpertId,
      selectedPackagedServiceId,
    });
    
    // Track submission success
    trackLeadFormSubmitSuccess();
    
    submissionSuccess.value = true;
    emit('success');
    return true;
    
  } catch (error: any) {
    console.error('[LeadSubmissionForm] Submit error:', error);
    submitError.value = error?.response?.data?.message || 'We could not submit your project. Please try again.';
    
    // Track submission error
    trackLeadFormSubmitError(error?.message || 'Unknown error');
    
    return false;
  }
}

// ==================
// Unsaved Changes Warning
// ==================

function handleBeforeUnload(event: BeforeUnloadEvent) {
  if (hasUnsavedChanges.value) {
    event.preventDefault();
    event.returnValue = ''; // Required for Chrome
    return ''; // Required for other browsers
  }
}

// Router navigation guard (component-level)
onBeforeRouteLeave((_to, _from, next) => {
  if (hasUnsavedChanges.value) {
    const confirmed = window.confirm(
      'You have unsaved changes. Are you sure you want to leave? Your progress will be lost.'
    );
    if (!confirmed) {
      next(false);
      return;
    }
  }
  next();
});

onMounted(() => {
  // Add beforeunload listener for browser navigation (refresh, close tab, etc.)
  window.addEventListener('beforeunload', handleBeforeUnload);

  // Track form started
  trackLeadFormStarted({
    expertSlug: props.expertSlug,
    packagedServiceSlug: props.packagedServiceSlug,
    source: window.location.pathname,
  });

  // Track initial step view
  trackLeadFormStepViewed('projectDetails', 1, totalSteps.value);
});

onBeforeUnmount(() => {
  // Remove beforeunload listener
  window.removeEventListener('beforeunload', handleBeforeUnload);

  // Track form abandoned if not successfully submitted
  if (!submissionSuccess.value && hasUnsavedChanges.value) {
    trackLeadFormAbandoned(currentState.value);
  }
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Step 1: Project Details -->
    <ProjectDetailsStep
      v-show="currentState === 'projectDetails'"
      v-model="projectDetailsData"
      :expert="expert ?? undefined"
      :peer-name="directPeerName"
      :packaged-service="packagedService ?? undefined"
      :is-loading-expert="Boolean(isLoadingExpert && expertSlug)"
      :current-step="1"
      :total-steps="totalSteps"
      :source="props.source"
      @next="handleProjectDetailsNext"
    >
      <template #close-button>
        <slot name="close-button" />
      </template>
    </ProjectDetailsStep>

    <!-- Step 2: Store Information -->
    <StoreInformationStep
      v-show="currentState === 'storeInformation'"
      v-model="storeInformationData"
      :current-step="2"
      :total-steps="totalSteps"
      :is-authenticated="isAuthenticatedClient"
      :stores="clientStores"
      :is-loading-stores="isLoadingStores"
      :selected-expert-name="selectedExpertName"
      @next="handleStoreInformationNext"
      @back="handleStoreInformationBack"
    >
      <template #close-button>
        <slot name="close-button" />
      </template>
    </StoreInformationStep>
    <div v-if="submitError && currentState !== 'success'" role="alert" class="mt-4">
      <UiText as="p" variant="p-small" class="text-red-600" :text="submitError" />
    </div>

    <!-- Step 3: Auth step (login/register for unauthenticated users) -->
    <AuthStep
      v-show="currentState === 'auth'"
      :current-step="3"
      :total-steps="totalSteps"
      :store-name="storeNameForPrefill"
      :store-url="storeUrlForPrefill"
      @authenticated="handleAuthenticated"
      @back="handleAuthBack"
    >
      <template #close-button>
        <slot name="close-button" />
      </template>
    </AuthStep>

    <!-- Success State (keep v-if for this one as we don't go back from success) -->
    <SuccessStep
      v-if="currentState === 'success'"
      :has-selected-expert="!!selectedExpertName"
      :expert-name="selectedExpertName"
      :disable-auto-redirect="disableAutoRedirect"
    />
  </div>
</template>
